import {createContext, useContext, useMemo} from 'react';
import type {Box} from '@packages/utilities/box';
import {Actions} from './actions';

import {
  type Pending,
  type State,
  type _State,
  type TutorialConfig,
  applyOpaque,
} from './types';

type TutorialControls = {
  start: (tutorial: string) => void;
  nextStep: () => void;
  previousStep: () => void;
  goToStep: (nextStepId: string) => void;
  dismiss: (tutorial: string, nextStepId?: string) => void;
};

type TutorialRuntimeStates = {
  anchorPosition: null | string;
  getAnchorNode: () => null | Element;
  canvasViewport: Box;
};

export type UseTutorialResult = [
  currentTutorial: TutorialConfig | null,
  pendingSteps: Pending['steps'],
  controls: TutorialControls,
  states: TutorialRuntimeStates,
];

export const InternalTutorialContext = createContext<{
  state: State;
  dispatch: (arg1?: any) => void;
  states: TutorialRuntimeStates;
}>({
  state: applyOpaque<_State, State>({
    tutorials: {},
    pending: {
      steps: null,
      tutorial: null,
      globalStateStepSnapshot: {},
      error: null,
    },
  }),
  dispatch: () => {},
  states: {
    anchorPosition: null,
    getAnchorNode: () => null,
    canvasViewport: {top: 0, left: 0, width: 0, height: 0},
  },
});

export function useTutorial(): UseTutorialResult {
  const {
    state: {pending, tutorials},
    dispatch,
    states,
  } = useContext(InternalTutorialContext);

  return [
    pending.tutorial ? (tutorials[pending.tutorial] ?? null) : null,
    pending.steps,
    useMemo(
      () => ({
        start(tutorial: string) {
          dispatch(Actions.initOnboarding({tutorial}));
        },
        nextStep() {
          dispatch(Actions.stepContinued());
        },
        previousStep() {
          dispatch(Actions.stepReversed());
        },
        goToStep(nextStepId: string) {
          dispatch(Actions.stepSkipped({nextStepId}));
        },
        dismiss(tutorial: string, nextStepId?: string) {
          dispatch(Actions.dismissOnboarding({tutorial, nextStepId}));
        },
      }),
      [dispatch]
    ),
    states,
  ];
}
